import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useGameStore } from '../../store/gameStore';
import { User, MapPin, Search, FileText } from 'lucide-react';

const ICONS = {
  suspects: User,
  locations: MapPin,
  clues: Search,
  documents: FileText,
};

const COLORS = {
  suspects: {
    border: 'border-l-red-500',
    bg: 'bg-red-500/10',
    icon: 'text-red-400',
  },
  locations: {
    border: 'border-l-green-500',
    bg: 'bg-green-500/10',
    icon: 'text-green-400',
  },
  clues: {
    border: 'border-l-yellow-500',
    bg: 'bg-yellow-500/10',
    icon: 'text-yellow-400',
  },
  documents: {
    border: 'border-l-blue-500',
    bg: 'bg-blue-500/10',
    icon: 'text-blue-400',
  },
};

const LABELS = {
  suspects: 'New Suspect Discovered',
  locations: 'New Location Unlocked',
  clues: 'New Clue Found',
  documents: 'New Document Obtained',
};

// Beyond this, extra discoveries collapse into a single counter row so a
// six-clue examination can't paper over the screen.
const MAX_VISIBLE = 3;

// Same accent map the ActionBar / modals use for their themed chrome.
const THEME_ACCENTS = {
  noir: '#DC143C',
  '90s_nyc': '#FF6B35',
  miami_vice: '#FF1493',
};

const queueKey = (item) => `${item?.type}:${item?.id}`;

/**
 * DiscoveryNotification - Toast notifications for newly discovered items
 * Displays in top-right corner with auto-dismiss.
 *
 * Fed by `animationQueue`, whose entries are produced by the single discovery
 * producer in gameStore.updateGameState (plus CipherModal's explicit reward
 * clue). Each type+id is toasted at most once per mounted session, and an
 * item whose display name cannot be resolved yet is skipped rather than shown
 * with an internal id.
 */
export const DiscoveryNotification = () => {
  const [notifications, setNotifications] = useState([]);
  const [dismissing, setDismissing] = useState(new Set());
  const timersRef = useRef(new Set());
  const seenRef = useRef(new Set());
  const animationQueue = useGameStore((state) => state.animationQueue);
  const dropFromAnimationQueue = useGameStore((state) => state.dropFromAnimationQueue);
  // A new case (or a restart of the same one) must start with a clean slate:
  // nothing already toasted may be suppressed, and nothing queued by the
  // previous case may be shown. `hydrationNonce` is monotonic and bumped by
  // every hydration door (including a featured restart, which keeps the
  // session id) — a wall clock would collide inside one millisecond.
  const sessionId = useGameStore((state) => state.sessionId);
  const hydrationNonce = useGameStore((state) => state.hydrationNonce);
  const caseEpoch = useGameStore((state) => state.caseEpoch);
  const selectedTheme = useGameStore((state) => state.selectedTheme);
  const themeAccent = THEME_ACCENTS[selectedTheme] || THEME_ACCENTS.noir;

  // Get item details from store
  const suspects = useGameStore((state) => state.suspects);
  const locations = useGameStore((state) => state.locations);
  const clues = useGameStore((state) => state.clues);
  const documents = useGameStore((state) => state.documents);

  // Returns null when the item isn't in state yet (e.g. a reveal that still
  // needs a session refresh) so the caller can skip it instead of printing an
  // internal id or a placeholder.
  const getItemName = useCallback((type, id) => {
    switch (type) {
      case 'suspects':
        return suspects.find(s => s.id === id)?.name || null;
      case 'locations':
        return locations.find(l => l.id === id)?.name || null;
      case 'clues':
        return clues.find(c => c.id === id)?.name || null;
      case 'documents': {
        const document = documents.find(d => d.id === id);
        return document?.title || document?.name || null;
      }
      default:
        return null;
    }
  }, [suspects, locations, clues, documents]);

  // Clear pending timers on unmount only. (Doing this in the queue effect's
  // cleanup would cancel in-flight auto-dismiss timers every time a new item
  // arrived, leaving earlier toasts on screen forever.)
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach(timer => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  // Reset when the store is re-hydrated underneath us. Declared before the
  // queue effect so a reset can never swallow a fresh discovery from the same
  // commit.
  //
  // Two levels, because not every hydration is a new case:
  //  - ANY hydration (refresh, restore, restart, new case) clears the dedupe
  //    set. The hydration door also empties `animationQueue`, so this only
  //    means "an item queued after this point may toast again" — which is
  //    exactly what a restart needs.
  //  - Only a CASE change (new case, restart, reset, different session) wipes
  //    what is on screen. A mid-action session refresh (`refetchSession` →
  //    `loadSavedGame` for the same session and case) must not snatch back a
  //    toast the player is still reading.
  const sessionRef = useRef(sessionId);
  const hydrationRef = useRef(hydrationNonce);
  const caseEpochRef = useRef(caseEpoch);
  useEffect(() => {
    const caseChanged = sessionRef.current !== sessionId || caseEpochRef.current !== caseEpoch;
    const hydrated = hydrationRef.current !== hydrationNonce;
    if (!caseChanged && !hydrated) return;
    sessionRef.current = sessionId;
    hydrationRef.current = hydrationNonce;
    caseEpochRef.current = caseEpoch;

    seenRef.current = new Set();
    if (!caseChanged) return;

    timersRef.current.forEach(timer => clearTimeout(timer));
    timersRef.current.clear();
    setNotifications([]);
    setDismissing(new Set());
  }, [sessionId, hydrationNonce, caseEpoch]);

  useEffect(() => {
    animationQueue.forEach(item => {
      const key = queueKey(item);
      if (seenRef.current.has(key)) return;

      const name = getItemName(item.type, item.id);
      // Not resolvable yet — leave it unseen so it can toast once the entity
      // arrives (this effect re-runs when the entity arrays change).
      if (!name) return;

      seenRef.current.add(key);

      setNotifications(prev => (
        prev.some(n => n.key === key)
          ? prev
          : [...prev, { key, type: item.type, id: item.id, name }]
      ));

      // Auto-dismiss after 4 seconds
      const dismissTimer = setTimeout(() => {
        timersRef.current.delete(dismissTimer);
        setDismissing(prevDismissing => new Set([...prevDismissing, key]));

        // Remove after exit animation
        const removeTimer = setTimeout(() => {
          timersRef.current.delete(removeTimer);
          setNotifications(prevNotifs => prevNotifs.filter(n => n.key !== key));
          setDismissing(prevDismissing => {
            const next = new Set(prevDismissing);
            next.delete(key);
            return next;
          });
          // Drain the entry: the queue is a work list, not a log.
          dropFromAnimationQueue?.(item.type, item.id);
        }, 300);
        timersRef.current.add(removeTimer);
      }, 4000);
      timersRef.current.add(dismissTimer);
    });
  }, [animationQueue, getItemName, dropFromAnimationQueue]);

  const visible = notifications.slice(0, MAX_VISIBLE);
  const overflow = notifications.length - visible.length;

  return (
    // Always mounted: a live region that appears with its first message is
    // not announced by screen readers.
    // Purely informational and self-dismissing, so the whole stack stays
    // click-through — it sits over the fixed desktop RightPanel and must not
    // create a 4s dead zone there.
    <div
      className="fixed top-4 right-4 z-50 space-y-2 pointer-events-none"
      role="log"
      aria-live="polite"
      aria-relevant="additions"
    >
      {visible.map((notification) => {
        const Icon = ICONS[notification.type] || Search;
        const colors = COLORS[notification.type] || COLORS.clues;
        const label = LABELS[notification.type] || 'New Discovery';
        const isDismissing = dismissing.has(notification.key);

        return (
          <div
            key={notification.key}
            className={`
              flex items-center gap-3 px-4 py-3 rounded-lg border border-l-4
              bg-slate-800/95 shadow-lg backdrop-blur-sm
              ${colors.border} ${colors.bg}
              ${isDismissing ? 'animate-slide-out-right' : 'animate-slide-in-right'}
            `}
            // Kind colour stays on the left bar; the theme accent tints the
            // remaining edges, exactly like the ActionBar's themed chrome.
            style={{
              borderTopColor: `${themeAccent}40`,
              borderRightColor: `${themeAccent}40`,
              borderBottomColor: `${themeAccent}40`,
            }}
          >
            <Icon className={`w-5 h-5 ${colors.icon}`} />
            <div>
              <span className="text-white font-medium text-sm block">{label}</span>
              <span className="text-gray-400 text-xs">{notification.name}</span>
            </div>
          </div>
        );
      })}

      {overflow > 0 && (
        <div className="flex items-center justify-end px-4 py-2 rounded-lg border-l-4 border-l-white/30 bg-slate-800/95 shadow-lg backdrop-blur-sm">
          <span className="text-gray-300 text-xs">
            +{overflow} more discover{overflow === 1 ? 'y' : 'ies'}
          </span>
        </div>
      )}
    </div>
  );
};

export default DiscoveryNotification;
